"use client";

import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import type {
  WorkOrder,
  SegmentPart,
  ScheduledItem,
  ScheduleState,
} from "../types";
import type { AppUser } from "../models";
import { getTechnicianDayCapacity } from "../utils/capacity-calculator";
import { SettingsLocation } from "models/lookupTypes";
import { ApplicationUser } from "components/QueueBoard/models/workOrderTypes";
import type {
  SettingsLocationBusinessHours,
  SettingsLocationHoliday,
} from "../models";
import type { EmployeeSchedule, EmployeeScheduleException } from "../models";
import SchedulingAPI from "../api/schedulingApi";
import { DAY_OF_WEEK_MAP } from "../utils/schedular-utils";
import {
  getCache,
  setCache,
  clearSchedulingLookupCaches,
} from "../../../common/Utils/cacheUtil";
import LookupAPI from "../../../QueueBoard/api/lookupDataApi";
import { DAYS } from "../utils/constants";
import { User } from "components/authentication/models/authTypes";
import localStorageUtil from "../../../../app/localStorage";
import { logAmplitudeEventWithProps } from "components/common/amplitude/amplitude";
import packageJson from "../../../../../package.json";

// HMR Detection: This timestamp changes after each hot reload
// Used to force cache resync after code changes in development
const USE_SCHEDULE_MODULE_TIMESTAMP = Date.now();

export function useSchedule() {
  const [state, setState] = useState<ScheduleState>(() => {
    // Initialize state with a function to ensure it's only called once
    console.log("useSchedule: Initializing state");
    return {
      technicians: [], // Will be set from global cache (technicians_userName)
      workOrders: [], // Will be fetched from database every time
      segmentParts: [],
      scheduledItems: [],
      reservedCapacity: new Map(),
      locations: [], // Will be set from global cache (locations_userName)
      businessHours: [], // Will be set from global cache (sch_locationBusinessHours), filtered by selected locationId
      holidays: [], // Will be set from global cache (sch_locationHolidays), filtered by selected locationId
      employeeSchedules: [], // Will be set from global cache (sch_employeeSchedules), filtered by technicianId and location
      employeeScheduleExceptions: [], // Will be set from global cache (sch_employeeScheduleExceptions), filtered by technicianId and location
    };
  });

  const scheduledItemsIndex = useMemo(() => {
    const index = new Map<string, ScheduledItem[]>();
    for (const item of state.scheduledItems) {
      const key = `${item.technicianId}:${item.day}`;
      const list = index.get(key) ?? [];
      list.push(item);
      index.set(key, list);
    }
    for (const items of index.values()) {
      items.sort((a, b) => {
        const aKey = a.sortKey ?? a.order * 1000.0;
        const bKey = b.sortKey ?? b.order * 1000.0;
        return aKey - bKey;
      });
    }
    return index;
  }, [state.scheduledItems]);

  // Keep a ref of latest state so async helpers can read items before setState applies.
  const stateRef = useRef(state);
  stateRef.current = state;

  // Save to localStorage - exclude workOrders, technicians, and locations (they should be fetched fresh)
  useEffect(() => {
    const serialized = {
      segmentParts: state.segmentParts,
      scheduledItems: state.scheduledItems,
      reservedCapacity: Array.from(state.reservedCapacity.entries()),
    };
    localStorage.setItem("schedule-state", JSON.stringify(serialized));
  }, [state.segmentParts, state.scheduledItems, state.reservedCapacity]);

  // Load from localStorage - only restore segmentParts, scheduledItems, and reservedCapacity
  // CRITICAL: This runs on mount and should NOT overwrite workOrders, technicians, or locations
  useEffect(() => {
    const saved = localStorage.getItem("schedule-state");
    if (saved) {
      const parsed = JSON.parse(saved);
      setState((prev) => ({
        ...prev,
        // CRITICAL: Only restore these fields, don't touch workOrders, technicians, or locations
        segmentParts: parsed.segmentParts || [],
        scheduledItems: parsed.scheduledItems || [],
        reservedCapacity: new Map(parsed.reservedCapacity || []),
      }));
    }
  }, []);

  // Track last module load timestamp to detect HMR
  const lastModuleLoadRef = useRef<number>(0);
  const cacheLoadAttemptRef = useRef<number>(0);
  const schedulingCacheRefreshInFlightRef = useRef(false);
  const [user] = useState<User | undefined>(localStorageUtil.getByKey("user"));
  // Load scheduling data from cache on mount (and after HMR)
  // This is the single source of truth for capacity calculations
  // CRITICAL: This effect must run after HMR to restore capacity calculation data
  // CRITICAL: This effect also polls if cache is initially empty (waiting for useLookupData to populate)
  useEffect(() => {
    const username = localStorage.getItem("username") || "";
    if (!username) return;

    // HMR Detection: If module timestamp changed, force a resync
    const isHmrReload =
      lastModuleLoadRef.current !== 0 &&
      lastModuleLoadRef.current !== USE_SCHEDULE_MODULE_TIMESTAMP;

    if (isHmrReload) {
      console.log(
        "[useSchedule] HMR detected - forcing cache resync for capacity calculations",
      );
    }
    lastModuleLoadRef.current = USE_SCHEDULE_MODULE_TIMESTAMP;

    // Same TTL as useLookupData; entries are cleared on login so each session starts fresh.
    const MAX_CACHE_AGE = 1000 * 60 * 60 * 24; // 24 hours

    const loadFromCache = (): boolean => {
      // Load business hours from cache
      const cachedBusinessHours = getCache<SettingsLocationBusinessHours[]>(
        "sch_locationBusinessHours",
        username,
        MAX_CACHE_AGE,
      );

      // Load holidays from cache
      const cachedHolidays = getCache<SettingsLocationHoliday[]>(
        "sch_locationHolidays",
        username,
        MAX_CACHE_AGE,
      );

      // Load employee schedules from cache
      const cachedEmployeeSchedules = getCache<EmployeeSchedule[]>(
        "sch_employeeSchedules",
        username,
        MAX_CACHE_AGE,
      );

      // Load employee schedule exceptions from cache
      const cachedEmployeeScheduleExceptions = getCache<
        EmployeeScheduleException[]
      >("sch_employeeScheduleExceptions", username, MAX_CACHE_AGE);

      // Check if we have the minimum required data
      const hasData =
        (cachedBusinessHours && cachedBusinessHours.length > 0) ||
        (cachedEmployeeSchedules && cachedEmployeeSchedules.length > 0);

      // Update state with cached data
      setState((prev) => ({
        ...prev,
        businessHours: cachedBusinessHours || prev.businessHours,
        holidays: cachedHolidays || prev.holidays,
        employeeSchedules: cachedEmployeeSchedules || prev.employeeSchedules,
        employeeScheduleExceptions:
          cachedEmployeeScheduleExceptions || prev.employeeScheduleExceptions,
      }));

      console.log("[useSchedule] Loaded scheduling data from cache:", {
        businessHours: cachedBusinessHours?.length || 0,
        holidays: cachedHolidays?.length || 0,
        employeeSchedules: cachedEmployeeSchedules?.length || 0,
        employeeScheduleExceptions:
          cachedEmployeeScheduleExceptions?.length || 0,
        hasData,
      });

      return hasData;
    };

    // Load immediately
    const dataLoaded = loadFromCache();

    // If data not loaded, poll every 500ms until it's available (max 15 seconds)
    // This handles the case where useLookupData is still fetching from API
    if (!dataLoaded && cacheLoadAttemptRef.current === 0) {
      cacheLoadAttemptRef.current = 1;
      let attempts = 0;
      const maxAttempts = 30; // 15 seconds max
      const pollInterval = setInterval(() => {
        attempts++;
        const loaded = loadFromCache();
        if (loaded || attempts >= maxAttempts) {
          clearInterval(pollInterval);
          if (attempts >= maxAttempts && !loaded) {
            console.warn(
              "[useSchedule] Max attempts reached loading scheduling data from cache",
            );
          }
        }
      }, 500);

      return () => clearInterval(pollInterval);
    }
  }, []);

  /**
   * Fetch store/employee schedule data from the database, replace the local
   * scheduling lookup cache, and apply it to schedule state so capacity
   * recalculates. Leaves cache and UI unchanged if the fetch fails.
   */
  const refreshSchedulingCache = useCallback(async () => {
    const username = localStorage.getItem("username") || "";
    if (!username || schedulingCacheRefreshInFlightRef.current) {
      return;
    }

    schedulingCacheRefreshInFlightRef.current = true;
    try {
      const [
        businessHours,
        holidays,
        employeeSchedules,
        employeeScheduleExceptions,
      ] = await Promise.all([
        LookupAPI.getAllLocationBusinessHours(),
        LookupAPI.getAllLocationHolidays(),
        LookupAPI.getAllEmployeeSchedules(),
        LookupAPI.getAllEmployeeScheduleExceptions(),
      ]);

      const nextBusinessHours = Array.isArray(businessHours)
        ? businessHours
        : [];
      const nextHolidays = Array.isArray(holidays) ? holidays : [];
      const nextEmployeeSchedules = Array.isArray(employeeSchedules)
        ? employeeSchedules
        : [];
      const nextEmployeeScheduleExceptions = Array.isArray(
        employeeScheduleExceptions,
      )
        ? employeeScheduleExceptions
        : [];

      // LookupAPI swallows network errors as []. Do not wipe a populated
      // cache/UI when both required datasets come back empty.
      const previousHadRequiredData =
        stateRef.current.businessHours.length > 0 ||
        stateRef.current.employeeSchedules.length > 0;
      const nextHasRequiredData =
        nextBusinessHours.length > 0 || nextEmployeeSchedules.length > 0;
      if (previousHadRequiredData && !nextHasRequiredData) {
        console.warn(
          "[useSchedule] Scheduling cache refresh returned no store/employee schedules; keeping existing data",
        );
        return;
      }

      clearSchedulingLookupCaches(username);
      setCache("sch_locationBusinessHours", nextBusinessHours, username);
      setCache("sch_locationHolidays", nextHolidays, username);
      setCache("sch_employeeSchedules", nextEmployeeSchedules, username);
      setCache(
        "sch_employeeScheduleExceptions",
        nextEmployeeScheduleExceptions,
        username,
      );

      setState((prev) => ({
        ...prev,
        businessHours: nextBusinessHours,
        holidays: nextHolidays,
        employeeSchedules: nextEmployeeSchedules,
        employeeScheduleExceptions: nextEmployeeScheduleExceptions,
      }));

      console.log("[useSchedule] Refreshed scheduling data from database:", {
        businessHours: nextBusinessHours.length,
        holidays: nextHolidays.length,
        employeeSchedules: nextEmployeeSchedules.length,
        employeeScheduleExceptions: nextEmployeeScheduleExceptions.length,
      });
    } catch (error) {
      console.error(
        "[useSchedule] Failed to refresh scheduling cache:",
        error,
      );
    } finally {
      schedulingCacheRefreshInFlightRef.current = false;
    }
  }, []);

  // Debug: Log when workOrders changes (only log, don't cause side effects)
  // CRITICAL: Removed to prevent infinite loops - uncomment only for debugging
  // useEffect(() => {
  //   console.log("workOrders state changed:", state.workOrders?.length || 0, "items");
  // }, [state.workOrders?.length]);

  /**
   * Calculate sortKey for inserting an item at a specific position
   * Uses decimal arithmetic to avoid recalculating all items
   */
  const calculateSortKey = useCallback(
    (existingItems: ScheduledItem[], insertPosition: number): number => {
      if (existingItems.length === 0) {
        return 1000.0; // Initial sortKey
      }

      // Sort by sortKey (fallback to order if sortKey not set)
      const sorted = [...existingItems].sort((a, b) => {
        const aKey = a.sortKey ?? a.order * 1000.0;
        const bKey = b.sortKey ?? b.order * 1000.0;
        return aKey - bKey;
      });

      if (insertPosition === 0) {
        // Insert at top: use first item's sortKey - 1
        const firstKey = sorted[0].sortKey ?? sorted[0].order * 1000.0;
        return firstKey - 1.0;
      } else if (insertPosition >= sorted.length) {
        // Insert at bottom: use last item's sortKey + 1
        const lastKey =
          sorted[sorted.length - 1].sortKey ??
          sorted[sorted.length - 1].order * 1000.0;
        return lastKey + 1.0;
      } else {
        // Insert between two items: average their sortKeys
        const beforeKey =
          sorted[insertPosition - 1].sortKey ??
          sorted[insertPosition - 1].order * 1000.0;
        const afterKey =
          sorted[insertPosition].sortKey ??
          sorted[insertPosition].order * 1000.0;
        return (beforeKey + afterKey) / 2.0;
      }
    },
    [],
  );

  const breakSegment = useCallback(
    (segmentId: string, partsOrCount: number | number[]) => {
      // Find the segment in any work order
      let segment: any = null;
      let workOrderId: string | null = null;
      for (const wo of state.workOrders) {
        const found = wo.segmentList.find((seg) => seg.id === segmentId);
        if (found) {
          segment = found;
          workOrderId = wo.id;
          break;
        }
      }
      if (!segment || !workOrderId) return;

      // CRITICAL: Use segment.estimatedHours (not segment.hours) for breaking segments
      const segmentHours = segment.estimatedHours || segment.hours || 0;
      const parts = Array.isArray(partsOrCount)
        ? partsOrCount
        : Array.from(
            { length: partsOrCount },
            () => segmentHours / partsOrCount,
          );
      const newParts: SegmentPart[] = [];

      // CRITICAL: Store the segment name in each part so it's available for displayText when scheduling
      const segmentName = segment.name || "";

      for (let i = 0; i < parts.length; i++) {
        newParts.push({
          id: `segment-part-${segmentId}-${Date.now()}-${i}`,
          parentSegmentId: segmentId,
          hours: parts[i],
          parentWorkOrderId: workOrderId,
          isUsed: false,
          segmentName: segmentName, // Store segment name for displayText
        });
      }

      setState((prev) => {
        const newReserved = new Map(prev.reservedCapacity);
        // Update reserved capacity for the work order
        // CRITICAL: Use the original segment's estimatedHours, not totalPartsHours
        // Breaking a segment should NOT modify the source segment
        const currentReserved = prev.reservedCapacity.get(workOrderId!) || 0;
        // Use segment.estimatedHours (original value) for reserved capacity calculation
        newReserved.set(workOrderId!, currentReserved + segment.estimatedHours);

        // CRITICAL: Do NOT modify the source segment
        // The source segment should remain unchanged when breaking it down
        // Only create new segment parts, don't modify the original segment
        return {
          ...prev,
          segmentParts: [...prev.segmentParts, ...newParts],
          reservedCapacity: newReserved,
          // Do NOT update workOrders - keep the source segment unchanged
        };
      });
    },
    [state.workOrders],
  );

  const scheduleItem = useCallback(
    (
      workOrderId: string,
      segmentPartId: string | undefined,
      technicianId: number,
      day: string,
      hours: number,
    ) => {
      setState((prev) => {
        // Get existing items for this tech/day to calculate sortKey
        const existingItems = prev.scheduledItems
          .filter((i) => i.technicianId === technicianId && i.day === day)
          .sort((a, b) => {
            const aKey = a.sortKey ?? a.order * 1000.0;
            const bKey = b.sortKey ?? b.order * 1000.0;
            return aKey - bKey;
          });

        // Calculate sortKey for adding to end
        let newSortKey: number;
        if (existingItems.length === 0) {
          newSortKey = 1000.0;
        } else {
          const lastKey =
            existingItems[existingItems.length - 1].sortKey ??
            existingItems[existingItems.length - 1].order * 1000.0;
          newSortKey = lastKey + 1.0;
        }

        const item: ScheduledItem = {
          id: `scheduled-${Date.now()}`,
          workOrderId,
          segmentPartId,
          technicianId,
          day: day as any,
          hours,
          order: existingItems.length, // Keep order for backward compatibility
          sortKey: newSortKey,
        };

        let newSegmentParts = prev.segmentParts;
        if (segmentPartId) {
          newSegmentParts = prev.segmentParts.map((part) =>
            part.id === segmentPartId ? { ...part, isUsed: true } : part,
          );
        }

        return {
          ...prev,
          scheduledItems: [...prev.scheduledItems, item],
          segmentParts: newSegmentParts,
        };
      });
    },
    [],
  );

  const scheduleFromDrag = useCallback(
    (
      dragData: any,
      technicianId: number,
      day: string,
      hours: number,
      insertAt?: number,
      options?: { overCapacity?: boolean; technicianOff?: boolean },
      workDate?: Date, // CRITICAL: Pass workDate to ensure correct date is saved
    ) => {
      // CRITICAL: displayText should ONLY contain the segment name (the name of the segment that was broken down)
      // Always get the segment name from the workOrder's segmentList
      let segmentName = "";
      let displayText = "";
      let description = "";
      // For segment parts, get the segment name from dragData first, then from the part itself (stored during breakSegment)
      // or fallback to finding it from the parent segment
      //debugger;
      if (dragData.type === "segment-part" && dragData.segmentId) {
        // First, try to get segment name from dragData (most reliable - set when dragging)
        if (dragData.segmentName) {
          segmentName = dragData.segmentName;
          displayText = segmentName; // displayText should ONLY have the segment name
        } else {
          // Fallback: Try to get segment name from the segment part itself
          const segmentPart = state.segmentParts.find(
            (part) => part.id === dragData.id,
          );
          if (segmentPart?.segmentName) {
            segmentName = segmentPart.segmentName;
            displayText = segmentName; // displayText should ONLY have the segment name
          } else {
            // Fallback: Find segment name from parent segment in workOrder
            const workOrder = state.workOrders.find(
              (wo) => wo.id === dragData.workOrderId,
            );
            if (workOrder) {
              const segment = workOrder.segmentList.find(
                (seg) => seg.id === dragData.segmentId,
              );
              if (segment) {
                segmentName = segment.name || "";
                displayText = segmentName; // displayText should ONLY have the segment name
              }
            }
          }
        }
      } else if (dragData.type === "segment") {
        // For regular segments, try dragData first, then fallback to state
        segmentName = dragData.segmentName || "";
        if (segmentName) {
          displayText = segmentName;
        } else {
          // Fallback: Try to find from state
          const workOrder = state.workOrders.find(
            (wo) => wo.id === dragData.workOrderId,
          );
          if (workOrder) {
            const segment = workOrder.segmentList.find(
              (seg) => seg.id === dragData.id,
            );
            if (segment) {
              segmentName = segment.name || "";
              displayText = segmentName;
            }
          }
        }
      }

      // CRITICAL: Set description based on type
      // Description follows pattern: 'Tech Scheduler Part: {number} - {hours} hours' for parts
      // or 'Tech Scheduler Segment' for regular segments
      if (dragData.type === "segment") {
        description = "Tech Scheduler Segment";
      } else if (dragData.type === "segment-part") {
        // For segment parts, find the part number and append hours
        const segmentPart = state.segmentParts.find(
          (part) => part.id === dragData.id,
        );

        if (segmentPart && dragData.segmentId) {
          // Find all parts for the same parent segment to determine part number
          const allPartsForSegment = state.segmentParts.filter(
            (part) => part.parentSegmentId === dragData.segmentId,
          );

          // Sort by creation order (based on ID timestamp/index) to get consistent part numbers
          // ID format: segment-part-{segmentId}-{timestamp}-{index}
          const sortedParts = [...allPartsForSegment].sort((a, b) => {
            // Extract the index from the ID (last number after final dash)
            const aMatch = a.id.match(/-(\d+)$/);
            const bMatch = b.id.match(/-(\d+)$/);
            const aIndex = aMatch ? parseInt(aMatch[1], 10) : 0;
            const bIndex = bMatch ? parseInt(bMatch[1], 10) : 0;
            return aIndex - bIndex;
          });

          // Find the index of this part (0-based) and convert to part number (1-based)
          const partIndex = sortedParts.findIndex(
            (part) => part.id === dragData.id,
          );
          const partNumber = partIndex >= 0 ? partIndex + 1 : 1;

          // Format hours: show as decimal (e.g., 5.0 for 5 hours, 2.5 for 2.5 hours)
          const hoursValue = segmentPart.hours.toFixed(1);
          description = `Tech Scheduler Part: ${partNumber} - ${hoursValue} hours`;
        } else {
          // Fallback if part not found in state - use hours from dragData
          const hoursValue = dragData.hours ? dragData.hours.toFixed(1) : "0.0";
          description = `Tech Scheduler Part: ${hoursValue} hours`;
        }
      }

      // CRITICAL: If displayText is still empty (can't find segment name), append fallback to description
      // displayText should remain empty - it should ONLY contain the segment name
      // The fallback value goes to description instead
      if (
        (dragData.type === "segment" || dragData.type === "segment-part") &&
        !displayText &&
        dragData.id
      ) {
        const fallbackText =
          dragData.type === "segment-part"
            ? `SegmentPart_${dragData.id}`
            : `Segment_${dragData.id}`;

        // Append fallback to description if we have a description, otherwise use it as description
        if (description) {
          description = `${description} - ${fallbackText}`;
        } else {
          description = fallbackText;
        }
      }

      // CRITICAL: Try to get work order details from workOrders array if available
      // This ensures WO#, customer name, etc. are available immediately without needing to refresh
      // If not found (e.g., from search results), fall back to dragData which includes these details
      let workOrderNumber: string | undefined = undefined;
      let invoiceNumber: string | undefined = undefined;
      let customerName: string | undefined = undefined;
      let customerNumber: string | undefined = undefined;
      let woStatusId: number | undefined = undefined;
      let myReference: string | undefined = undefined;
      let woDescription: string | undefined = undefined;
      let unitModel: string | undefined = undefined;
      let vinNumber: string | undefined = undefined;
      let unitNumber: string | undefined = undefined;
      let woPriorityId: number | undefined = undefined;

      if (dragData.workOrderId) {
        const workOrder = state.workOrders.find(
          (wo) => wo.id === dragData.workOrderId,
        );
        if (workOrder) {
          workOrderNumber = workOrder.workOrderNumber;
          invoiceNumber = workOrder.invoiceNumber;
          customerName = workOrder.customerName;
          customerNumber = workOrder.customerNumber;
          woStatusId = workOrder.woStatusId;
          myReference = workOrder.woDisplayText || workOrder.myReference;
          woDescription = workOrder.description;
          unitModel = workOrder.unitModel;
          vinNumber = workOrder.vinNumber;
          unitNumber = workOrder.unitNumber;
          woPriorityId = workOrder.woPriorityId;
        }
      }
      
      // CRITICAL: Fall back to dragData for work order details if not found in state
      // This ensures details are available when dragging segments from search results
      workOrderNumber = workOrderNumber || dragData.workOrderNumber;
      invoiceNumber = invoiceNumber || dragData.invoiceNumber;
      customerName = customerName || dragData.customerName;
      customerNumber = customerNumber || dragData.customerNumber;
      woStatusId = woStatusId ?? dragData.woStatusId;
      myReference = myReference || dragData.myReference || dragData.woDisplayText;
      woDescription = woDescription || dragData.woDescription;
      unitModel = unitModel || dragData.unitModel;
      vinNumber = vinNumber || dragData.vinNumber;
      unitNumber = unitNumber || dragData.unitNumber;
      woPriorityId = woPriorityId ?? dragData.woPriorityId;

      // CRITICAL: For segment parts, we need both segmentId (parent) and segmentPartId
      // segmentId is needed to save the ScheduleItem correctly in the database
      const segmentId =
        dragData.type === "segment"
          ? dragData.id
          : dragData.type === "segment-part"
            ? dragData.segmentId // Use parentSegmentId from dragData
            : undefined;

      // CRITICAL: Calculate workDate if not provided
      // Use provided workDate (from week offset) or calculate from day (current week fallback)
      let calculatedWorkDate: Date;
      if (workDate) {
        calculatedWorkDate = new Date(workDate);
        calculatedWorkDate.setHours(0, 0, 0, 0); // Ensure time is set to start of day
      } else {
        // Fallback: calculate from day (current week) - should rarely happen
        const today = new Date();
        const currentDay = today.getDay();
        const targetDay = DAY_OF_WEEK_MAP[day] ?? 1;
        const diff = targetDay - currentDay;
        calculatedWorkDate = new Date(today);
        calculatedWorkDate.setDate(today.getDate() + diff);
        calculatedWorkDate.setHours(0, 0, 0, 0);
      }

      const item: ScheduledItem = {
        id: `scheduled-${Date.now()}`,
        workOrderId: dragData.workOrderId,
        segmentId: segmentId, // CRITICAL: Include segmentId for both segments and segment parts
        segmentPartId:
          dragData.type === "segment-part" ? dragData.id : undefined,
        technicianId,
        day: day as any,
        hours,
        order: insertAt !== undefined ? insertAt : Date.now(),
        sortKey: 0, // Will be calculated below
        overCapacity: options?.overCapacity,
        technicianOff: options?.technicianOff,
        displayText: displayText, // Store displayText from dragData
        description: description, // Store description from dragData
        workOrderNumber: workOrderNumber, // Store workOrderNumber if available from workOrders
        invoiceNumber: invoiceNumber, // Store invoiceNumber if available from workOrders
        workDate: calculatedWorkDate, // CRITICAL: Set workDate to the correct date for the selected week
        // Include work order details for display in technician card
        customerName: customerName,
        customerNumber: customerNumber,
        woStatusId: woStatusId,
        myReference: myReference,
        woDescription: woDescription,
        unitModel: unitModel,
        vinNumber: vinNumber,
        unitNumber: unitNumber,
        woPriorityId: woPriorityId,
      };
      //debugger;
      let newSortKey: number = 1000.0;
      let itemWithSortKey: ScheduledItem = item;

      setState((prev) => {
        let newSegmentParts = prev.segmentParts;
        if (item.segmentPartId) {
          newSegmentParts = prev.segmentParts.map((part) =>
            part.id === item.segmentPartId ? { ...part, isUsed: true } : part,
          );
        }

        // Get existing items for this tech/day, sorted by sortKey (fallback to order)
        const existingItems = prev.scheduledItems
          .filter((i) => i.technicianId === technicianId && i.day === day)
          .sort((a, b) => {
            const aKey = a.sortKey ?? a.order * 1000.0;
            const bKey = b.sortKey ?? b.order * 1000.0;
            return aKey - bKey;
          });

        // Calculate sortKey based on insert position
        if (
          insertAt !== undefined &&
          insertAt >= 0 &&
          insertAt <= existingItems.length
        ) {
          newSortKey = calculateSortKey(existingItems, insertAt);
        } else {
          // Add to end (default behavior)
          if (existingItems.length === 0) {
            newSortKey = 1000.0;
          } else {
            const lastKey =
              existingItems[existingItems.length - 1].sortKey ??
              existingItems[existingItems.length - 1].order * 1000.0;
            newSortKey = lastKey + 1.0;
          }
        }

        // Create item with sortKey (and order for backward compatibility)
        itemWithSortKey = {
          ...item,
          sortKey: newSortKey,
          order: insertAt !== undefined ? insertAt : existingItems.length, // Keep order for backward compatibility
        };

        // Only add the new item - no need to recalculate existing items
        const updatedScheduledItems = [...prev.scheduledItems, itemWithSortKey];

        // Update reserved capacity
        const newReserved = new Map(prev.reservedCapacity);
        const currentReserved = newReserved.get(item.workOrderId) || 0;
        newReserved.set(item.workOrderId, currentReserved + item.hours);

        // CRITICAL: Remove the placed segment from the work order's segmentList
        // If work order has no remaining segments after this, remove the entire work order
        let updatedWorkOrders = prev.workOrders;
        if (
          dragData.type === "segment" &&
          dragData.id &&
          dragData.workOrderId
        ) {
          updatedWorkOrders = prev.workOrders
            .map((wo) => {
              if (wo.id === dragData.workOrderId) {
                // Remove the placed segment from segmentList
                const filteredSegments = wo.segmentList.filter(
                  (seg) => seg.id !== dragData.id,
                );
                return { ...wo, segmentList: filteredSegments };
              }
              return wo;
            })
            .filter((wo) => {
              // Remove work orders with no remaining segments
              return wo.segmentList.length > 0;
            });
        }

        return {
          ...prev,
          scheduledItems: updatedScheduledItems,
          segmentParts: newSegmentParts,
          reservedCapacity: newReserved,
          workOrders: updatedWorkOrders,
        };
      });

      // Persist to backend API
      (async () => {
        try {
          // CRITICAL: Use the workDate from the item (already calculated with week offset)
          // This ensures the correct date is saved to the database
          const workDate = itemWithSortKey.workDate || new Date();

          // Use displayText and description from the item (already set from dragData or state lookup)
          const displayText = itemWithSortKey.displayText || "";
          const description = itemWithSortKey.description || "";

          // CRITICAL: Segment parts are saved as regular schedule items
          // Each part is saved with the parent segmentId (not segmentPartId)
          // The segmentPartId is only used on the frontend to track which parts are used
          // In the database, each part becomes a separate schedule item linked to the parent segment
          const scheduleItemId = await SchedulingAPI.createScheduleItem({
            segmentId: item.segmentId ? parseInt(item.segmentId) : undefined,
            segmentPartId: undefined, // Don't save segmentPartId - parts are saved as regular schedule items
            workOrderId: parseInt(item.workOrderId),
            technicianId: item.technicianId,
            workDate: workDate,
            sortKey: newSortKey,
            estimatedHours: item.hours,
            overCapacity: item.overCapacity,
            technicianOff: item.technicianOff,
            displayText: displayText,
            description: description,
          });

          logAmplitudeEventWithProps(
            user.userNameAndCompany,
            {
              company: user.company,
              user: user.userName,
            },
            "createTechCalendarScheduleItem",
            {
              elapsed_ms: "124",
              title: "Add Tech Schedule",
              componentName: "use-schedule.tsx",
              version: packageJson.version,
            },
          );

          // Update the item with the scheduleItemId from the database
          setState((prev) => ({
            ...prev,
            scheduledItems: prev.scheduledItems.map((i) =>
              i.id === itemWithSortKey.id ? { ...i, scheduleItemId } : i,
            ),
          }));
        } catch (error) {
          console.error("Failed to create schedule item in backend:", error);
          // Optionally show user notification here
          logAmplitudeEventWithProps(
            user.userNameAndCompany,
            {
              company: user.company,
              user: user.userName,
            },
            "createTechCalendarScheduleItem",
            {
              elapsed_ms: "124",
              title: "Add Tech Schedule - Exception",
              componentName: "use-schedule.tsx " + error,
              version: packageJson.version,
            },
          );
        }
      })();
    },
    [calculateSortKey, state.workOrders],
  );

  /**
   * Schedule all segments from a work order at once
   * When a work order is dropped on a technician card, this creates scheduled items for each segment
   * Each segment becomes its own ScheduledItem with the segment name as displayText
   */
  const scheduleWorkOrder = useCallback(
    (
      workOrderId: string,
      segments: { id: string; name: string; estimatedHours: number }[],
      technicianId: number,
      day: string,
      workDate?: Date,
      options?: { overCapacity?: boolean; technicianOff?: boolean },
      // Work order details passed from drag data (for when work order isn't in state)
      workOrderDetails?: {
        workOrderNumber?: string;
        customerName?: string;
        customerNumber?: string;
        woStatusId?: number;
        myReference?: string;
      },
    ) => {
      if (!segments || segments.length === 0) {
        console.warn("scheduleWorkOrder: No segments to schedule");
        return;
      }

      // Get work order details - prefer dragData values, fallback to state
      // dragData values are guaranteed to be from the work order being dragged
      const workOrder = state.workOrders.find((wo) => wo.id === workOrderId);
      const workOrderNumber =
        workOrderDetails?.workOrderNumber || workOrder?.workOrderNumber;
      const invoiceNumber = workOrder?.invoiceNumber;
      const customerName =
        workOrderDetails?.customerName || workOrder?.customerName;
      const customerNumber =
        workOrderDetails?.customerNumber || workOrder?.customerNumber;
      const woStatusId = workOrderDetails?.woStatusId || workOrder?.woStatusId;
      const myReference =
        workOrderDetails?.myReference ||
        workOrder?.woDisplayText ||
        workOrder?.myReference;
      const woDescription = workOrder?.description;
      const unitModel = workOrder?.unitModel;
      const vinNumber = workOrder?.vinNumber;
      const unitNumber = workOrder?.unitNumber;
      const woPriorityId = workOrder?.woPriorityId;

      // CRITICAL: Calculate workDate if not provided
      let calculatedWorkDate: Date;
      if (workDate) {
        calculatedWorkDate = new Date(workDate);
        calculatedWorkDate.setHours(0, 0, 0, 0);
      } else {
        const today = new Date();
        const currentDay = today.getDay();
        const targetDay = DAY_OF_WEEK_MAP[day] ?? 1;
        const diff = targetDay - currentDay;
        calculatedWorkDate = new Date(today);
        calculatedWorkDate.setDate(today.getDate() + diff);
        calculatedWorkDate.setHours(0, 0, 0, 0);
      }

      // Create scheduled items for each segment
      const newScheduledItems: ScheduledItem[] = [];

      setState((prev) => {
        // Get existing items for this tech/day, sorted by sortKey
        const existingItems = prev.scheduledItems
          .filter((i) => i.technicianId === technicianId && i.day === day)
          .sort((a, b) => {
            const aKey = a.sortKey ?? a.order * 1000.0;
            const bKey = b.sortKey ?? b.order * 1000.0;
            return aKey - bKey;
          });

        // Calculate initial sortKey for adding to end
        let currentSortKey: number;
        if (existingItems.length === 0) {
          currentSortKey = 1000.0;
        } else {
          const lastKey =
            existingItems[existingItems.length - 1].sortKey ??
            existingItems[existingItems.length - 1].order * 1000.0;
          currentSortKey = lastKey + 1.0;
        }

        // Create scheduled items for each segment
        // Each segment becomes its own scheduled item with segment name as displayText
        const baseTimestamp = Date.now();
        segments.forEach((segment, index) => {
          const item: ScheduledItem = {
            id: `scheduled-${baseTimestamp}-seg-${index}`,
            workOrderId: workOrderId,
            segmentId: segment.id,
            segmentPartId: undefined,
            technicianId,
            day: day as any,
            hours: segment.estimatedHours,
            order: existingItems.length + index,
            sortKey: currentSortKey + index,
            overCapacity: options?.overCapacity,
            technicianOff: options?.technicianOff,
            // CRITICAL: displayText is the SEGMENT name, not work order name
            displayText: segment.name,
            description: "Tech Scheduler Segment",
            workOrderNumber: workOrderNumber,
            invoiceNumber: invoiceNumber,
            workDate: calculatedWorkDate,
            // Include work order details for display
            customerName: customerName,
            customerNumber: customerNumber,
            woStatusId: woStatusId,
            myReference: myReference,
            woDescription: woDescription,
            unitModel: unitModel,
            vinNumber: vinNumber,
            unitNumber: unitNumber,
            woPriorityId: woPriorityId,
          };
          newScheduledItems.push(item);
        });

        // Update reserved capacity for the work order
        const totalHours = segments.reduce(
          (sum, seg) => sum + seg.estimatedHours,
          0,
        );
        const newReserved = new Map(prev.reservedCapacity);
        const currentReserved = newReserved.get(workOrderId) || 0;
        newReserved.set(workOrderId, currentReserved + totalHours);

        // CRITICAL: Remove the work order from sidebar when all its segments are scheduled
        // Since we're scheduling all segments at once, the work order should be removed entirely
        const updatedWorkOrders = prev.workOrders.filter(
          (wo) => wo.id !== workOrderId,
        );

        return {
          ...prev,
          scheduledItems: [...prev.scheduledItems, ...newScheduledItems],
          reservedCapacity: newReserved,
          workOrders: updatedWorkOrders,
        };
      });

      // Persist each segment to backend API
      (async () => {
        for (const item of newScheduledItems) {
          try {
            const scheduleItemId = await SchedulingAPI.createScheduleItem({
              segmentId: item.segmentId ? parseInt(item.segmentId) : undefined,
              segmentPartId: undefined,
              workOrderId: parseInt(item.workOrderId),
              technicianId: item.technicianId,
              workDate: item.workDate || calculatedWorkDate,
              sortKey: item.sortKey,
              estimatedHours: item.hours,
              overCapacity: item.overCapacity,
              technicianOff: item.technicianOff,
              displayText: item.displayText || "",
              description: item.description || "",
            });

            // Update the item with the scheduleItemId from the database
            setState((prev) => ({
              ...prev,
              scheduledItems: prev.scheduledItems.map((i) =>
                i.id === item.id ? { ...i, scheduleItemId } : i,
              ),
            }));
          } catch (error) {
            console.error(
              "Failed to create schedule item for segment in backend:",
              error,
            );
          }
        }
      })();
    },
    [state.workOrders],
  );

  const removeScheduledItem = useCallback(
    async (itemId: string): Promise<void> => {
      // Read from ref BEFORE setState — updater may not run until after this function
      // continues, which previously caused group "Remove all" to skip every API delete.
      const removedItem = stateRef.current.scheduledItems.find(
        (i) => i.id === itemId,
      );
      if (!removedItem) {
        return;
      }

      setState((prev) => {
        let newSegmentParts = prev.segmentParts;

        if (removedItem.segmentPartId) {
          newSegmentParts = prev.segmentParts.map((part) =>
            part.id === removedItem.segmentPartId
              ? { ...part, isUsed: false }
              : part,
          );
        }

        return {
          ...prev,
          scheduledItems: prev.scheduledItems.filter((i) => i.id !== itemId),
          segmentParts: newSegmentParts,
        };
      });

      if (removedItem.scheduleItemId) {
        try {
          await SchedulingAPI.removeScheduleItem(removedItem.scheduleItemId);

          logAmplitudeEventWithProps(
            user.userNameAndCompany,
            {
              company: user.company,
              user: user.userName,
            },
            "removeTechCalendarScheduleItem",
            {
              elapsed_ms: "0.00",
              title: "Remove Tech Schedule",
              componentName: "use-schedule.tsx",
              version: packageJson.version,
            },
          );
        } catch (error) {
          console.error("Failed to remove schedule item in backend:", error);

          logAmplitudeEventWithProps(
            user.userNameAndCompany,
            {
              company: user.company,
              user: user.userName,
            },
            "removeTechCalendarScheduleItem",
            {
              elapsed_ms: "0.00",
              title: "Remove Tech Schedule - Exception",
              componentName: "use-schedule.tsx " + error,
              version: packageJson.version,
            },
          );
        }
      }
    },
    [],
  );

  /** Remove many schedule items in one UI update, then delete each on the server. */
  const removeScheduledItems = useCallback(
    async (itemIds: string[]): Promise<void> => {
      if (!itemIds.length) return;

      const idSet = new Set(itemIds);
      const toRemove = stateRef.current.scheduledItems.filter((i) =>
        idSet.has(i.id),
      );
      if (toRemove.length === 0) return;

      const partIdsToFree = new Set(
        toRemove
          .map((i) => i.segmentPartId)
          .filter((id): id is string => Boolean(id)),
      );
      const apiIds = toRemove
        .map((i) => i.scheduleItemId)
        .filter((id): id is number => typeof id === "number" && id > 0);

      setState((prev) => ({
        ...prev,
        scheduledItems: prev.scheduledItems.filter((i) => !idSet.has(i.id)),
        segmentParts:
          partIdsToFree.size === 0
            ? prev.segmentParts
            : prev.segmentParts.map((part) =>
                partIdsToFree.has(part.id) ? { ...part, isUsed: false } : part,
              ),
      }));

      await Promise.all(
        apiIds.map(async (scheduleItemId) => {
          try {
            await SchedulingAPI.removeScheduleItem(scheduleItemId);
            logAmplitudeEventWithProps(
              user.userNameAndCompany,
              {
                company: user.company,
                user: user.userName,
              },
              "removeTechCalendarScheduleItem",
              {
                elapsed_ms: "0.00",
                title: "Remove Tech Schedule",
                componentName: "use-schedule.tsx",
                version: packageJson.version,
              },
            );
          } catch (error) {
            console.error("Failed to remove schedule item in backend:", error);
            logAmplitudeEventWithProps(
              user.userNameAndCompany,
              {
                company: user.company,
                user: user.userName,
              },
              "removeTechCalendarScheduleItem",
              {
                elapsed_ms: "0.00",
                title: "Remove Tech Schedule - Exception",
                componentName: "use-schedule.tsx " + error,
                version: packageJson.version,
              },
            );
          }
        }),
      );
    },
    [],
  );

  const completeScheduledItem = useCallback(
    async (itemId: string): Promise<void> => {
      const completedItem = stateRef.current.scheduledItems.find(
        (i) => i.id === itemId,
      );

      if (!completedItem?.scheduleItemId || !user?.identity) {
        return;
      }

      try {
        await SchedulingAPI.completeScheduleItem({
          scheduleItemId: completedItem.scheduleItemId,
          completedByUserId: user.identity,
        });

        setState((prev) => ({
          ...prev,
          scheduledItems: prev.scheduledItems.filter((i) => i.id !== itemId),
        }));

        logAmplitudeEventWithProps(
          user.userNameAndCompany,
          {
            company: user.company,
            user: user.userName,
          },
          "completeTechCalendarScheduleItem",
          {
            elapsed_ms: "0.00",
            title: "Complete Tech Schedule Item",
            componentName: "use-schedule.tsx",
            version: packageJson.version,
          },
        );
      } catch (error) {
        console.error("Failed to complete schedule item in backend:", error);
        throw error;
      }
    },
    [user],
  );

  /**
   * Reorder items using sortKey - only updates the moved item, not all items
   * This is optimized to only re-render the source and target items
   */
  const reorderScheduledItems = useCallback(
    (
      technicianId: number,
      day: string,
      fromItemId: string,
      toSortKey: number,
    ) => {
      let movedItem: ScheduledItem | undefined;

      setState((prev) => {
        // Find the item being moved
        movedItem = prev.scheduledItems.find(
          (item) =>
            item.id === fromItemId &&
            item.technicianId === technicianId &&
            item.day === day,
        );

        if (!movedItem) {
          return prev;
        }

        // Only update the moved item's sortKey - no need to recalculate all items
        const updatedScheduled = prev.scheduledItems.map((item) => {
          if (
            item.id === fromItemId &&
            item.technicianId === technicianId &&
            item.day === day
          ) {
            return { ...item, sortKey: toSortKey };
          }
          return item;
        });

        return {
          ...prev,
          scheduledItems: updatedScheduled,
        };
      });

      // Persist to backend API
      if (movedItem?.scheduleItemId) {
        (async () => {
          try {
            await SchedulingAPI.updateScheduleItem({
              scheduleItemId: movedItem!.scheduleItemId!,
              sortKey: toSortKey,
            });

            logAmplitudeEventWithProps(
              user.userNameAndCompany,
              {
                company: user.company,
                user: user.userName,
              },
              "updateTechCalendarScheduleItem",
              {
                elapsed_ms: "0.00",
                title: "Update Tech Schedule",
                componentName: "use-schedule.tsx",
                version: packageJson.version,
              },
            );
          } catch (error) {
            console.error("Failed to update schedule item in backend:", error);
            // Optionally show user notification here

            logAmplitudeEventWithProps(
              user.userNameAndCompany,
              {
                company: user.company,
                user: user.userName,
              },
              "updateTechCalendarScheduleItem",
              {
                elapsed_ms: "0.00",
                title: "Update Tech Schedule - Exception",
                componentName: "use-schedule.tsx " + error,
                version: packageJson.version,
              },
            );
          }
        })();
      }
    },
    [],
  );

  const moveScheduledItem = useCallback(
    async (
      itemId: string,
      newTechnicianId: number,
      newDay: string,
      workDate?: Date,
      seed?: {
        hours?: number;
        workOrderId?: string;
        segmentId?: string;
        displayText?: string;
        description?: string;
        workOrderNumber?: string;
        invoiceNumber?: string;
        customerName?: string;
        customerNumber?: string;
        woStatusId?: number;
        woDescription?: string;
        woPriorityId?: number;
        unitNumber?: string;
        unitModel?: string;
        vinNumber?: string;
        myReference?: string;
      },
      insertAtPosition?: number,
    ): Promise<boolean> => {
      let calculatedWorkDate: Date;

      if (workDate) {
        calculatedWorkDate = new Date(workDate);
        calculatedWorkDate.setHours(0, 0, 0, 0);
      } else {
        const today = new Date();
        const currentDay = today.getDay();
        const targetDay = DAY_OF_WEEK_MAP[newDay] ?? 1;
        const diff = targetDay - currentDay;
        calculatedWorkDate = new Date(today);
        calculatedWorkDate.setDate(today.getDate() + diff);
        calculatedWorkDate.setHours(0, 0, 0, 0);
      }

      const numericIdMatch = String(itemId).match(/(\d+)\s*$/);
      const numericScheduleItemId = numericIdMatch
        ? Number(numericIdMatch[1])
        : undefined;

      const prevItems = stateRef.current.scheduledItems;
      const existing = prevItems.find(
        (i) =>
          i.id === itemId ||
          (numericScheduleItemId != null &&
            i.scheduleItemId === numericScheduleItemId) ||
          i.id === `scheduled-${itemId}`,
      );

      const resolvedScheduleItemId =
        existing?.scheduleItemId ?? numericScheduleItemId;
      if (!resolvedScheduleItemId) {
        return false;
      }

      const itemsWithoutMoved = prevItems.filter(
        (i) =>
          i.id !== existing?.id &&
          i.scheduleItemId !== resolvedScheduleItemId &&
          i.id !== `scheduled-${resolvedScheduleItemId}`,
      );

      const itemsInNewLocation = itemsWithoutMoved
        .filter((i) => i.technicianId === newTechnicianId && i.day === newDay)
        .sort((a, b) => {
          const aKey = a.sortKey ?? a.order * 1000.0;
          const bKey = b.sortKey ?? b.order * 1000.0;
          return aKey - bKey;
        });

      const newSortKey =
        insertAtPosition !== undefined && insertAtPosition >= 0
          ? calculateSortKey(itemsInNewLocation, insertAtPosition)
          : itemsInNewLocation.length === 0
            ? 1000.0
            : (itemsInNewLocation[itemsInNewLocation.length - 1].sortKey ??
                itemsInNewLocation[itemsInNewLocation.length - 1].order *
                  1000.0) + 1.0;

      const updatedItem: ScheduledItem = existing
        ? {
            ...existing,
            technicianId: newTechnicianId,
            day: newDay as ScheduledItem["day"],
            sortKey: newSortKey,
            order: itemsInNewLocation.length,
            workDate: calculatedWorkDate,
            scheduleItemId: resolvedScheduleItemId,
          }
        : {
            id: `scheduled-${resolvedScheduleItemId}`,
            scheduleItemId: resolvedScheduleItemId,
            workOrderId: seed?.workOrderId ?? "",
            segmentId: seed?.segmentId,
            technicianId: newTechnicianId,
            day: newDay as ScheduledItem["day"],
            hours: seed?.hours ?? 0,
            order: itemsInNewLocation.length,
            sortKey: newSortKey,
            workDate: calculatedWorkDate,
            displayText: seed?.displayText,
            description: seed?.description,
            workOrderNumber: seed?.workOrderNumber,
            invoiceNumber: seed?.invoiceNumber,
            customerName: seed?.customerName,
            customerNumber: seed?.customerNumber,
            woStatusId: seed?.woStatusId,
            woDescription: seed?.woDescription,
            woPriorityId: seed?.woPriorityId,
            unitNumber: seed?.unitNumber,
            unitModel: seed?.unitModel,
            vinNumber: seed?.vinNumber,
            myReference: seed?.myReference,
          };

      // Optimistic: move or insert so past-due items from the sidebar appear immediately.
      setState((prev) => ({
        ...prev,
        scheduledItems: [
          ...prev.scheduledItems.filter(
            (i) =>
              i.id !== updatedItem.id &&
              i.scheduleItemId !== resolvedScheduleItemId,
          ),
          updatedItem,
        ],
      }));

      try {
        await SchedulingAPI.updateScheduleItem({
          scheduleItemId: resolvedScheduleItemId,
          technicianId: newTechnicianId,
          workDate: calculatedWorkDate,
          sortKey: newSortKey,
        });

        logAmplitudeEventWithProps(
          user.userNameAndCompany,
          {
            company: user.company,
            user: user.userName,
          },
          "updateTechCalendarScheduleItem",
          {
            elapsed_ms: "0.00",
            title: "Update Tech Schedule",
            componentName: "use-schedule.tsx",
            version: packageJson.version,
          },
        );
        return true;
      } catch (error) {
        console.error("Failed to update schedule item in backend:", error);

        logAmplitudeEventWithProps(
          user.userNameAndCompany,
          {
            company: user.company,
            user: user.userName,
          },
          "updateTechCalendarScheduleItem",
          {
            elapsed_ms: "0.00",
            title: "Update Tech Schedule - Exception",
            componentName: "use-schedule.tsx " + error,
            version: packageJson.version,
          },
        );
        return false;
      }
    },
    [calculateSortKey],
  );

  /**
   * Update the estimated hours of a single scheduled item (inline edit on the technician card).
   * - Optimistically updates local state (scheduledItems + reservedCapacity delta) so derived
   *   selectors like getAvailableHours / getBaseCapacity recompute immediately.
   * - Persists the change via SchedulingAPI.updateScheduleItem which updates both
   *   TechnicianScheduleItem.EstimatedHours and InvoiceSegmentTask.EstimatedHours server-side.
   * - Rolls back the optimistic change on failure and re-throws so the UI can show an error.
   */
  const updateScheduledItemHours = useCallback(
    async (itemId: string, newHours: number): Promise<void> => {
      const previousItem = state.scheduledItems.find((i) => i.id === itemId);
      if (!previousItem) {
        throw new Error(`Scheduled item not found: ${itemId}`);
      }

      if (!previousItem.scheduleItemId) {
        throw new Error(
          "Schedule item has not been persisted yet (missing scheduleItemId).",
        );
      }

      if (newHours <= 0 || newHours === previousItem.hours) {
        return;
      }

      const previousHours = previousItem.hours;
      const workOrderId = previousItem.workOrderId;
      const delta = newHours - previousHours;

      // Optimistic update: patch hours + adjust reserved capacity by delta.
      setState((prev) => {
        const newScheduledItems = prev.scheduledItems.map((i) =>
          i.id === itemId ? { ...i, hours: newHours } : i,
        );

        const newReserved = new Map(prev.reservedCapacity);
        const currentReserved = newReserved.get(workOrderId) || 0;
        newReserved.set(workOrderId, Math.max(0, currentReserved + delta));

        return {
          ...prev,
          scheduledItems: newScheduledItems,
          reservedCapacity: newReserved,
        };
      });

      try {
        await SchedulingAPI.updateScheduleItem({
          scheduleItemId: previousItem.scheduleItemId,
          estimatedHours: newHours,
        });

        if (user) {
          logAmplitudeEventWithProps(
            user.userNameAndCompany,
            {
              company: user.company,
              user: user.userName,
            },
            "updateTechCalendarScheduleItemHours",
            {
              elapsed_ms: "0.00",
              title: "Update Tech Schedule Hours",
              componentName: "use-schedule.tsx",
              version: packageJson.version,
            },
          );
        }
      } catch (error) {
        // Roll back optimistic update on failure.
        setState((prev) => {
          const revertedItems = prev.scheduledItems.map((i) =>
            i.id === itemId ? { ...i, hours: previousHours } : i,
          );

          const revertedReserved = new Map(prev.reservedCapacity);
          const currentReserved = revertedReserved.get(workOrderId) || 0;
          revertedReserved.set(
            workOrderId,
            Math.max(0, currentReserved - delta),
          );

          return {
            ...prev,
            scheduledItems: revertedItems,
            reservedCapacity: revertedReserved,
          };
        });

        console.error("Failed to update scheduled item hours:", error);

        if (user) {
          logAmplitudeEventWithProps(
            user.userNameAndCompany,
            {
              company: user.company,
              user: user.userName,
            },
            "updateTechCalendarScheduleItemHours",
            {
              elapsed_ms: "0.00",
              title: "Update Tech Schedule Hours - Exception",
              componentName: "use-schedule.tsx " + error,
              version: packageJson.version,
            },
          );
        }

        throw error;
      }
    },
    [state.scheduledItems, user],
  );

  const updateWorkOrderHours = useCallback(
    (workOrderId: string, newHours: number) => {
      setState((prev) => ({
        ...prev,
        workOrders: prev.workOrders.map((wo) =>
          wo.id === workOrderId ? { ...wo, estimatedHours: newHours } : wo,
        ),
      }));
    },
    [],
  );

  const updateSegmentHours = useCallback(
    (segmentId: string, newHours: number) => {
      setState((prev) => {
        const updatedWorkOrders = prev.workOrders.map((wo) => {
          const segmentIndex = wo.segmentList.findIndex(
            (seg) => seg.id === segmentId,
          );
          if (segmentIndex !== -1) {
            const updatedSegments = [...wo.segmentList];
            updatedSegments[segmentIndex] = {
              ...updatedSegments[segmentIndex],
              hours: newHours,
              estimatedHours: newHours,
            };
            return {
              ...wo,
              segmentList: updatedSegments,
            };
          }
          return wo;
        });
        return {
          ...prev,
          workOrders: updatedWorkOrders,
        };
      });
    },
    [],
  );

  const removeBreakdown = useCallback((workOrderId: string) => {
    setState((prev) => {
      // Remove all segment parts for this work order
      const remainingParts = prev.segmentParts.filter(
        (part) => part.parentWorkOrderId !== workOrderId,
      );

      // Remove any scheduled items that use these segment parts
      const partIds = prev.segmentParts
        .filter((part) => part.parentWorkOrderId === workOrderId)
        .map((part) => part.id);
      const remainingScheduledItems = prev.scheduledItems.filter(
        (item) => !item.segmentPartId || !partIds.includes(item.segmentPartId),
      );

      // Remove reserved capacity for this work order
      const newReserved = new Map(prev.reservedCapacity);
      newReserved.delete(workOrderId);

      return {
        ...prev,
        segmentParts: remainingParts,
        scheduledItems: remainingScheduledItems,
        reservedCapacity: newReserved,
      };
    });
  }, []);

  const isWorkOrderBroken = (workOrderId: string): boolean => {
    return state.segmentParts.some(
      (part) => part.parentWorkOrderId === workOrderId,
    );
  };

  const isSegmentBroken = useCallback(
    (segmentId: string): boolean => {
      return state.segmentParts.some(
        (part) => part.parentSegmentId === segmentId,
      );
    },
    [state.segmentParts],
  );

  /**
   * CAPACITY CALCULATION: Get Base Capacity for a Technician on a Specific Day
   *
   * This function calculates the base capacity (available hours) for a technician on a given day.
   * It is called by CapacityBoard for each technician/day combination to display capacity.
   *
   * Scenarios:
   *   1. First Page Load:
   *      - Called after initialDataLoaded = true
   *      - Uses data synced from cache/store
   *      - Returns 0 if data not ready (guards prevent this)
   *
   *   2. Page Refresh:
   *      - Called after initialDataLoaded = true
   *      - Uses data synced from cache/store (not seed data)
   *      - Ensures accurate capacity calculations
   *
   *   3. Date Provided:
   *      - Uses getTechnicianDayCapacity utility function
   *      - Accounts for employee schedules AND exceptions
   *      - Exception can modify capacity (e.g., vacation = 0 hours, modified hours)
   *
   *   4. No Date Provided (Fallback):
   *      - Calculates from employee schedule only
   *      - Does not account for exceptions
   *      - Used when date is not available
   *
   * Data Requirements:
   *   - state.technicians: Must contain the technician
   *   - state.employeeSchedules: Must contain schedule for technician/day
   *   - state.employeeScheduleExceptions: Used if date is provided
   *
   * Returns: Number of available hours (0 if technician not found, no schedule, or invalid data)
   */
  const getBaseCapacity = useCallback((
    technicianId: number,
    day: string,
    date?: Date,
  ): number => {
    // Debug breakpoint: base capacity calculation (initial load / refresh)
    // This will hit whenever capacity is calculated for a technician/day
    // on first landing on the component or on page refresh.
    //debugger;

    // Guard: Ensure required data is loaded before calculating capacity
    // This prevents calculations with incomplete data (e.g., during initial load or refresh)
    if (
      state.technicians.length === 0 ||
      state.employeeSchedules.length === 0
    ) {
      return 0;
    }

    const technician = state.technicians.find(
      (t) => t.identity === technicianId,
    );
    if (!technician) return 0;

    const dayOfWeekMap: { [key: string]: number } = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
    };
    const dayOfWeek = dayOfWeekMap[day] ?? 1;

    // Calculate capacity using utility function
    if (date) {
      // Ensure employeeScheduleExceptions is available (can be empty array)
      return getTechnicianDayCapacity(
        technician.identity,
        dayOfWeek,
        date,
        technician.defaultLocationIdentity,
        state.employeeSchedules,
        state.employeeScheduleExceptions || [], // Provide empty array if undefined
      );
    } else {
      // Fallback: find schedule for this day
      const schedule = state.employeeSchedules.find(
        (es) =>
          es.appUserId === technician.identity && es.dayOfWeek === dayOfWeek,
      );
      if (schedule && schedule.beginTime && schedule.endTime) {
        // Handle both Date objects and date strings
        const begin =
          schedule.beginTime instanceof Date
            ? schedule.beginTime
            : new Date(schedule.beginTime);
        const end =
          schedule.endTime instanceof Date
            ? schedule.endTime
            : new Date(schedule.endTime);

        if (isNaN(begin.getTime()) || isNaN(end.getTime())) {
          return 0;
        }

        const diffMs = end.getTime() - begin.getTime();
        return diffMs / (1000 * 60 * 60);
      }
    }
    return 0;
  }, [
    state.technicians,
    state.employeeSchedules,
    state.employeeScheduleExceptions,
  ]);

  /**
   * CAPACITY CALCULATION: Get Available Hours (Remaining Capacity) for a Technician on a Specific Day
   *
   * This function calculates the remaining available hours after subtracting scheduled items.
   * It is called by CapacityBoard to show how much capacity is remaining for each technician/day.
   *
   * Formula: Available Hours = Base Capacity - Scheduled Hours
   *
   * Scenarios:
   *   1. First Page Load:
   *      - Called after initialDataLoaded = true
   *      - Uses data synced from cache/store
   *      - Scheduled items loaded from API (fetchScheduledItems)
   *
   *   2. Page Refresh:
   *      - Called after initialDataLoaded = true
   *      - Uses data synced from cache/store
   *      - Scheduled items loaded from API (fetchScheduledItems)
   *      - Ensures accurate remaining capacity display
   *
   *   3. After Scheduling an Item:
   *      - Scheduled items updated in state
   *      - Available hours recalculated automatically
   *      - Shows updated remaining capacity
   *
   *   4. After Removing an Item:
   *      - Scheduled items updated in state
   *      - Available hours recalculated automatically
   *      - Shows increased remaining capacity
   *
   * Data Requirements:
   *   - state.technicians: Must contain the technician
   *   - state.scheduledItems: Used to calculate scheduled hours
   *   - getBaseCapacity: Called to get base capacity
   *
   * Returns: Number of available hours remaining (0 if no capacity or over-capacity)
   */
  const getAvailableHours = useCallback((
    technicianId: number,
    day: string,
    date?: Date,
  ): number => {
    // Debug breakpoint: available hours calculation (remaining capacity)
    // This will hit after base capacity is calculated and scheduled items are applied.
    //debugger;

    // Guard: Ensure required data is loaded before calculating capacity
    // This prevents calculations with incomplete data (e.g., during initial load or refresh)
    if (state.technicians.length === 0) {
      return 0;
    }

    const technician = state.technicians.find(
      (t) => t.identity === technicianId,
    );
    if (!technician) return 0;

    const scheduled =
      scheduledItemsIndex.get(`${technicianId}:${day}`) ?? [];
    const used = scheduled.reduce((sum, item) => sum + item.hours, 0);

    // Get base capacity
    const baseCapacity = getBaseCapacity(technicianId, day, date);

    return Math.max(0, baseCapacity - used);
  }, [state.technicians, scheduledItemsIndex, getBaseCapacity]);

  const getWorkOrderReservedHours = (workOrderId: string): number => {
    return state.reservedCapacity.get(workOrderId) || 0;
  };

  const getScheduledItemsForTechDay = useCallback((
    technicianId: number,
    day: string,
  ): ScheduledItem[] => {
    return scheduledItemsIndex.get(`${technicianId}:${day}`) ?? [];
  }, [scheduledItemsIndex]);

  // Setter methods for workOrders, technicians, and locations (fetched from database/cache)
  // CRITICAL: Always create new array/object references to ensure React detects changes
  // CRITICAL: Only update if the data actually changed to prevent infinite loops
  const setWorkOrders = useCallback((workOrders: WorkOrder[]) => {
    setState((prev) => {
      // Create a new array reference even if contents are the same
      // This ensures React detects the state change and triggers re-renders
      const newWorkOrders = Array.isArray(workOrders)
        ? [...workOrders]
        : workOrders;

      const getWorkOrderSignature = (items: WorkOrder[]) =>
        items
          .map((wo) => {
            const segmentSignature = (wo.segmentList || [])
              .map(
                (seg) =>
                  `${seg.id}:${seg.name || ""}:${seg.estimatedHours ?? ""}:${
                    seg.hours ?? ""
                  }:${seg.schedulingStatus ?? ""}:${seg.scheduledHours ?? ""}:${
                    seg.remainingHours ?? ""
                  }:${seg.taskCount ?? 0}`,
              )
              .sort()
              .join("|");

            return `${wo.id}:${wo.workOrderNumber || ""}:${
              wo.invoiceNumber || ""
            }:${segmentSignature}`;
          })
          .sort()
          .join(",");

      if (
        prev.workOrders.length === newWorkOrders.length &&
        getWorkOrderSignature(prev.workOrders) ===
          getWorkOrderSignature(newWorkOrders)
      ) {
        return prev;
      }

      return {
        ...prev,
        workOrders: newWorkOrders,
      };
    });
  }, []);

  const setTechnicians = useCallback((technicians: ApplicationUser[]) => {
    setState((prev) => {
      const newTechnicians = Array.isArray(technicians)
        ? [...technicians]
        : technicians;
      return {
        ...prev,
        technicians: newTechnicians,
      };
    });
  }, []);

  const setLocations = useCallback((locations: SettingsLocation[]) => {
    setState((prev) => {
      const newLocations = Array.isArray(locations)
        ? [...locations]
        : locations;
      return {
        ...prev,
        locations: newLocations,
      };
    });
  }, []);

  const setEmployeeSchedules = useCallback(
    (employeeSchedules: EmployeeSchedule[]) => {
      setState((prev) => {
        const newEmployeeSchedules = Array.isArray(employeeSchedules)
          ? [...employeeSchedules]
          : employeeSchedules;
        return {
          ...prev,
          employeeSchedules: newEmployeeSchedules,
        };
      });
    },
    [],
  );

  const setEmployeeScheduleExceptions = useCallback(
    (employeeScheduleExceptions: EmployeeScheduleException[]) => {
      setState((prev) => {
        const newExceptions = Array.isArray(employeeScheduleExceptions)
          ? [...employeeScheduleExceptions]
          : employeeScheduleExceptions;
        return {
          ...prev,
          employeeScheduleExceptions: newExceptions,
        };
      });
    },
    [],
  );

  const setBusinessHours = useCallback(
    (businessHours: SettingsLocationBusinessHours[]) => {
      setState((prev) => {
        const newBusinessHours = Array.isArray(businessHours)
          ? [...businessHours]
          : businessHours;
        return {
          ...prev,
          businessHours: newBusinessHours,
        };
      });
    },
    [],
  );

  const setHolidays = useCallback((holidays: SettingsLocationHoliday[]) => {
    setState((prev) => {
      const newHolidays = Array.isArray(holidays) ? [...holidays] : holidays;
      return {
        ...prev,
        holidays: newHolidays,
      };
    });
  }, []);

  const setScheduledItems = useCallback((scheduledItems: ScheduledItem[]) => {
    setState((prev) => {
      const newScheduledItems = Array.isArray(scheduledItems)
        ? [...scheduledItems]
        : scheduledItems;
      return {
        ...prev,
        scheduledItems: newScheduledItems,
      };
    });
  }, []);

  return {
    state,
    breakSegment,
    scheduleItem,
    scheduleFromDrag,
    scheduleWorkOrder,
    removeScheduledItem,
    removeScheduledItems,
    completeScheduledItem,
    reorderScheduledItems,
    moveScheduledItem,
    updateScheduledItemHours,
    updateWorkOrderHours,
    updateSegmentHours,
    removeBreakdown,
    isWorkOrderBroken,
    isSegmentBroken,
    getBaseCapacity,
    getAvailableHours,
    getWorkOrderReservedHours,
    getScheduledItemsForTechDay,
    scheduledItemsIndex,
    setWorkOrders,
    setTechnicians,
    setLocations,
    setEmployeeSchedules,
    setEmployeeScheduleExceptions,
    setBusinessHours,
    setHolidays,
    setScheduledItems,
    refreshSchedulingCache,
    calculateSortKey, // Export for use in components
    DAYS,
  };
}
